import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  loadChatHistory,
  loadOlderMessages,
  setActiveChat,
  setActiveChatNull,
} from "../../actions/chatActions";
import ChatHeader from "./ChatHeader";
import MessageList from "./MessageList";
import MessageInput from "./MessageInput";
import { CHAT_LIST_UPDATE, USER_STOPPED_TYPING } from "../../constants/chatConstants";
import { wsManager } from "../../websocket/wsManager";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { selectIdentity } from "../../selectors/userSelectors";


export default function ChatPanel({ chatId }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // R5.8-e: identity-only subscription via memoized selectIdentity. Previously
  // this component subscribed to state.userLogin.userInfo directly, whose
  // object reference churned on every mergeUserInfo write (token refresh, B2
  // spreads, profile patches, follow/unfollow). The mark-read effect's
  // dep array uses the integer id (not the object), so effect *cadence* was
  // already stable, but the component itself re-rendered unnecessarily on
  // every userLogin mutation. selectIdentity memoization eliminates that
  // re-render without changing the underlying integer value used downstream.
  const identity = useSelector(selectIdentity);
  const userId = identity?.userId ?? null;
  const messages = useSelector(s => s.chat.messagesByChat[chatId] || []);
  const chat = useSelector(s => s.chat.chats.find(c => String(c.id) === String(chatId)));
  // const activeChatId = useSelector(s => s.chat.activeChatId || null);
  const historyState = useSelector(s => s.chat.historyByChat[chatId] || {});
  const typingUsers = useSelector(state => state.chat.typingByChat[chatId] || {});
  const typingUsersArray = Object.values(typingUsers);
  const sentReadIdsRef = useRef(new Set());
  const error = useSelector((state) => state.chat.error);

  useEffect(() => {
    if (!chatId) return;
    sentReadIdsRef.current.clear();
    dispatch(setActiveChat(chatId));
    dispatch(loadChatHistory(chatId));

    wsManager.send({
      event: "mark_read",
      chat_id: chatId,
      message_ids: [],
    });

    dispatch({
      type: CHAT_LIST_UPDATE,
      payload: {
        chat_id: chatId,
        reset_unread: true,
      },
    });

    return () => {
      dispatch(setActiveChatNull());
    };
  }, [chatId, dispatch]);

  useEffect(() => {
    // LS-1 fix: collect timeout ids and clear them from the useEffect's own
    // cleanup. Previously the `return clearTimeout` lived inside the forEach
    // callback (whose return value is discarded), so timers accumulated on
    // every typingUsers ref change.
    const timeouts = Object.values(typingUsers).map(user =>
      setTimeout(() => {
        dispatch({
          type: USER_STOPPED_TYPING,
          payload: { chatId, userId: user.id },
        });
      }, 2500)
    );

    return () => {
      timeouts.forEach(clearTimeout);
    };
  }, [typingUsers, chatId, dispatch]);

  useEffect(() => {
    if (!messages.length) return;

    const unreadIds = messages
      .filter(m => {
        if (m.type === "system") return false;

        const senderId = m.sender?.id;
        if (!senderId) return false;

        return (
          String(senderId) !== String(userId) &&
          !m.seen_by?.includes(String(userId)) &&
          !sentReadIdsRef.current.has(String(m.id))
        );
      })
      .map(m => m.id);

    if (unreadIds.length) {
      unreadIds.forEach(id => sentReadIdsRef.current.add(id));

      wsManager.send({
        event: "mark_read",
        chat_id: chatId,
        message_ids: unreadIds,
      });
    }
  }, [messages, chatId, userId]);

  const handleLoadOlder = () => {
    dispatch(loadOlderMessages(chatId));
  };

  // R8.2.7 — error-driven navigate moved out of render body. Previously
  // `if (error) { toast; navigate }` ran on every render where error
  // was truthy: each render → toast re-fired → navigate re-fired → on
  // re-render with the same error ref it ran again. Wrapped in useEffect
  // keyed on the error reference so a single error transition fires once.
  // Local containment only — no new action, no error reducer change.
  useEffect(() => {
    if (error) {
      toast.error(error.detail);
      navigate('/chats/');
    }
  }, [error, navigate]);

  return (
    <section className="chat-section">
      <div className="chat">
        <ChatHeader chat={chat} />
        <MessageList
          messages={messages}
          typingUsersArray={typingUsersArray}
          hasOlder={historyState.hasOlder}
          loadingOlder={historyState.loadingOlder}
          onLoadOlder={handleLoadOlder}
        />
        {/*{typingUsersArray.length > 0 && (*/}
        {/*  <div className="w-100 ps-3 text-sm">*/}
        {/*    {typingUsersArray*/}
        {/*      .map(u => u.firstname || u.email)*/}
        {/*      .join(", ")}{" "}*/}
        {/*    {typingUsersArray.length === 1 ? "друкує…" : "друкують…"}*/}
        {/*  </div>*/}
        {/*)}*/}
        <MessageInput
          chatId={chatId}
          isGroup={Boolean(chat?.is_group)}
          messagingBlocked={!chat?.is_group && chat?.messaging_available === false}
          blockedByMe={Boolean(chat?.blocked_by_me)}
        />
      </div>
    </section>
  );
}
